import React from "react";
import { Link } from "react-router";
import styled, { css } from "styled-components";
import { Path } from "@core/routing/path";
import { ImageVersions } from "@domain/media/dtos";
import { isVideo } from "@domain/media/utils";
import { Post } from "@domain/post/post";
import { mobile } from "@ui/styles/utils";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { stack } from "@ui/components/layout";
import { ImageView } from "@ui/components/media/imageView";
import { MosaicView } from "@ui/components/media/mosaicView";
import { PdfViewer } from "@ui/components/media/pdf/pdfViewer";
import { TooshareVideoPlayer } from "@ui/components/media/tooshareVideoPlayer";
import { ActionInfos } from "./actionInfos";
import { PostActions } from "./postActions";
import { PostContentDeleted } from "./postCourseDeleted";
import { PostOpenGraphView } from "./postOpenGraphView";
import { PostReference } from "@ui/components/post/reference/postReference";

interface PostContentProps extends React.HTMLAttributes<HTMLDivElement> {
	post?: Post;
	onCommentAction?: () => void;
	readonly?: boolean;
}

export const PostContent: React.FC<PostContentProps> = ({ post, onCommentAction, readonly, ...htmlProps }) => {
	if (!post) {
		return (
			<Container>
				<PostContentDeleted type="post" />
			</Container>
		);
	}

	const { text, assets, urlPreview, reference, type } = post;
	return (
		<Container {...htmlProps}>
			{type !== "SHARE_LIVE_COURSE" && text.hasText() && (
				<Link to={Path.Post(post.id)}>
					<DraftRenderer contractibleFold fold content={text} />
				</Link>
			)}
			{type !== "SHARE_LIVE_COURSE" && type !== "SHARE_APPLICATION" ? (
				assets?.length > 1 ? (
					<AssetsMosaicView images={assets} />
				) : assets?.length === 1 ? (
					assets[0].mimeType === "application/pdf" ? (
						<PdfViewer source={assets[0].url} pdfHeight={600} />
					) : isVideo(assets[0]) ? (
						<TooshareVideoPlayer asset={assets[0]} />
					) : (
						<StyledImageView image={assets[0]} imageStyle={assetStyle} imageVersion={ImageVersions.MEDIUM} />
					)
				) : null
			) : null}
			{type === "SHARE_CAROUSEL" &&
				reference &&
				reference.asset &&
				reference.asset.url &&
				reference.asset.mimeType === "application/pdf" && (
					<PdfViewer
						refTitle={reference.title}
						refId={reference.id}
						refSlug={reference.slug}
						refCategory={reference.category}
						source={reference.asset.url}
						pdfHeight={600}
					/>
				)}
			{type === "SHARE_CAROUSEL" && !reference && <PostContentDeleted type="carousel" />}
			{!!urlPreview && <PostOpenGraphView data={urlPreview} />}
			{type !== "BASIC" && <PostReference post={post} />}
			{!readonly && (
				<>
					<ActionInfos post={post} />
					<PostActions post={post} onCommentAction={onCommentAction} />
				</>
			)}
		</Container>
	);
};

const Container = styled.div`
	${({ theme }) => theme.textStyles.secondary};
	display: flex;
	flex-direction: column;
	${stack(20, "flex-start", "stretch")};
`;

const AssetsMosaicView = styled(MosaicView)`
	width: 100%;
	height: 400px;
	border-radius: 9px;
	align-self: center;
	@media ${mobile} {
		height: 230px;
	}
`;

const StyledImageView = styled(ImageView)`
	align-self: center;
`;

const assetStyle = css`
	max-width: 100%;
	max-height: 400px;
	border-radius: 9px;
	display: flex;
	margin: auto;
`;
